import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Clock, Star, Users } from 'lucide-react'
import BackgroundSlideshow from '../components/BackgroundSlideshow'
import TopNavigation from '../../components/TopNavigation.jsx'
import useAsset, { getAssetUrl } from '../../hooks/useAsset'

const LandingPage: React.FC = () => {
  const [currentTheme, setCurrentTheme] = useState('light')

  // Cache-busted logo assets
  const logoWhite = useAsset('/tailtations-white.svg')
  const logoDark = useAsset('/tailtations.svg')

  // Background slideshow images
  const slideshowImages = [
    getAssetUrl('/images/TT-IMG1.jpg'),
    getAssetUrl('/images/TT-IMG2.jpg'),
    getAssetUrl('/images/TT-IMG3.jpg'),
    getAssetUrl('/images/TT-IMG4.jpg'),
    getAssetUrl('/images/TT-IMG5.jpg')
  ]

  // Update theme when it changes
  useEffect(() => {
    const updateTheme = () => {
      const isDark = document.documentElement.classList.contains('dark')
      setCurrentTheme(isDark ? 'dark' : 'light')
    }

    updateTheme()

    const observer = new MutationObserver(updateTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen-safe relative">
      {/* Navigation - Home link goes to main Tailtations site */}
      <div className="relative z-50">
        <TopNavigation 
          backTo="https://www.tailtations.com" 
          backText="Home"
          showBackButton={true}
          hideLogo={true}
        />
      </div>

      {/* Background Slideshow */}
      <BackgroundSlideshow 
        images={slideshowImages} 
        interval={6000}
      />
      
      {/* Hero Section */}
      <section className="relative z-10 px-4 sm:px-6 lg:px-8 py-20 lg:py-32 -mt-[90px] lg:-mt-[135px]">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo/Brand */}
          <div className="flex justify-center items-center mb-8">
            <img
              src={currentTheme === 'dark' ? logoWhite : logoDark}
              alt="Tailtations"
              className="h-32 w-auto"
            />
          </div>

          {/* Tagline */}
          <p className="text-xl text-secret-accent-gold mb-4 font-medium">
            Tailtations Exclusive Access Program
          </p>

          {/* Hero Text */}
          <h1 className="text-4xl lg:text-6xl font-black text-foreground mb-8 leading-tight">
            Be the First to Know.<br />
            <span className="text-primary">Skip the Line.</span>
          </h1>

          <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
            Join the exclusive SECRET program for advance notice of popup events, 
            guaranteed pre-orders, and VIP access to our famous original fried lobster tails.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/register"
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 text-lg font-semibold flex items-center space-x-2 group rounded-md transition-colors shadow-sm"
            >
              <span>Join SECRET Program</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <Link
              to="/login"
              className="border border-border text-foreground hover:bg-muted px-8 py-4 text-lg font-semibold rounded-md transition-colors"
            >
              Member Login
            </Link>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10 opacity-10">
          <div className="absolute top-20 left-10 w-2 h-2 bg-primary rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-1 h-1 bg-secret-accent-gold rounded-full animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-1/4 w-1.5 h-1.5 bg-primary rounded-full animate-pulse delay-500"></div>
          <div className="absolute bottom-40 right-1/3 w-1 h-1 bg-secret-accent-gold rounded-full animate-pulse delay-1500"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-black text-center text-black dark:text-white mb-16">
            Why Join <span className="text-primary">SECRET</span>?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1 */}
            <div className={`${
              currentTheme === 'dark' 
                ? 'bg-black/20 backdrop-blur-sm border border-white/20' 
                : 'bg-white/30 backdrop-blur-sm border border-white/40'
            } rounded-lg p-6 shadow-lg text-center group hover:scale-105 transition-all duration-300`}>
              <div className="w-16 h-16 bg-secret-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Early Access</h3>
              <p className="text-white/80">
                Get notified up to 1 week before popup events. Never miss your favorite location again.
              </p>
            </div>

            {/* Feature 2 */}
            <div className={`${
              currentTheme === 'dark' 
                ? 'bg-black/20 backdrop-blur-sm border border-white/20' 
                : 'bg-white/30 backdrop-blur-sm border border-white/40'
            } rounded-lg p-6 shadow-lg text-center group hover:scale-105 transition-all duration-300`}>
              <div className="w-16 h-16 bg-secret-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">VIP Treatment</h3>
              <p className="text-white/80">
                Skip the line with dedicated pickup windows and exclusive menu items only for members.
              </p>
            </div>

            {/* Feature 3 */}
            <div className={`${
              currentTheme === 'dark' 
                ? 'bg-black/20 backdrop-blur-sm border border-white/20' 
                : 'bg-white/30 backdrop-blur-sm border border-white/40'
            } rounded-lg p-6 shadow-lg text-center group hover:scale-105 transition-all duration-300`}>
              <div className="w-16 h-16 bg-secret-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Guaranteed Orders</h3>
              <p className="text-white/80">
                Pre-order your favorites and guarantee availability. Transfer orders to friends easily.
              </p>
            </div>

            {/* Feature 4 */}
            <div className={`${
              currentTheme === 'dark' 
                ? 'bg-black/20 backdrop-blur-sm border border-white/20' 
                : 'bg-white/30 backdrop-blur-sm border border-white/40'
            } rounded-lg p-6 shadow-lg text-center group hover:scale-105 transition-all duration-300`}>
              <div className="w-16 h-16 bg-secret-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Exclusive Community</h3>
              <p className="text-white/80">
                Join a select group of Tailtations' most passionate fans with insider access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-black text-black dark:text-white mb-6">
            Ready to Join the <span className="text-primary">Elite</span>?
          </h2>
          <p className="text-xl text-white/80 mb-12">
            SECRET membership is exclusive. Join now and become part of the Tailtations inner circle.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-5 text-lg font-semibold flex items-center justify-center space-x-3 group rounded-md transition-colors shadow-sm"
            >
              <span>Get SECRET Access</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <p className="text-sm text-muted-foreground mt-8">
            Already a member? <Link to="/login" className="text-primary hover:text-primary/80 underline">Sign in here</Link>
          </p>
        </div>
      </section>

    </div>
  )
}

export default LandingPage